import type { DatasetRequest, DatasetResponse } from '@/libs/search/types';
import { request } from '@/api/request';
import type { Answer } from '@/modules/profile/api/client';
import { type Connection, ConnectionSourceType, ConnectionStatus } from '@/types/models/connection';
import type { ConnectionRequest } from '@/types/models/connectionRequests';
import type { Role } from '@/types/models/roles';
import type { User } from '@/types/models/user';
import type { OrganisationOwnershipStatus } from '@/types/models/organisation';
import type { DocumentDatasetResponse } from '@/modules/documents/types';
import type { Asset } from '@/types/models/monitoring';
import type { TermsOfServiceAgreementWUserData } from '@/types/models/termsOfService';

export type ZeusAssessmentData = {
  initialAssessmentComplete: boolean;
  scopingComplete: boolean;
  initialAssessmentCompleteAt?: string;
  reassessmentDueAt?: string;
  reassessmentStatus: string;
  reassessmentProgress?: number;
  initialAssessmentProgress?: number;
};

export type ZeusOrgData = {
  org: ZeusOrganisation;
  activeUsers: User[];
  activeSupplierConnections: ZeusActiveConnection[];
  activeClientConnections: ZeusActiveConnection[];
  activeSubEntityConnections: ZeusActiveConnection[];
  pendingConnectionRequests: ConnectionRequest[];
  pendingInvites: OrgInvite[];
  ssoStatus: SSOStatus;
  assessment: ZeusAssessmentData;
  assessmentRemindersPause: boolean;
  trial: Record<string, any>;
  tosAgreement: TermsOfServiceAgreementWUserData;
  externalMonitoringAssets: Asset[];
};

export type ZeusOrganisation = {
  id: string;
  name: string;
  tradingNames: string[];
  companyNumber: string; // company registration number, may be empty
  streetAddress: string;
  city: string;
  postalCode: string;
  location: string;
  sector: string;
  website: string;
  logoUrl: string;
  businessPhoneNumber: string;
  // signup source
  signupSourceType?: string | null;
  signupSourceID?: string | null;
  // statuses
  status: string;
  isSupplier: boolean;
  isClient: boolean;
  isFederated: boolean;
  isPopular: boolean;
  isHidden: boolean;
  isSuspended: boolean;
  isPaid: boolean;
  isClientRestricted: boolean;
  ownershipStatus: OrganisationOwnershipStatus;
  metatdata: {
    ip: string;
    ipCountry: string;
    httpReferrer: string;
  };
  createdAt: string;
  activatedAt?: string | null;
  deactivatedAt?: string | null;
  supplierAt?: string | null;
  clientAt?: string | null;
  modifiedAt?: string | null;
  modifiedBy?: string | null;
  deletedAt?: string | null;
  deletedBy?: string | null;
};

// Called OrganisationPartial on the backend. This is exclusively used in Zeus,
// and contains very little information of each org.
export type ZeusOrganisationLite = {
  id: string;
  name: string;
  logoUrl: string;
  isClient: boolean;
  isSupplier: boolean;
  isFederated: boolean;
  createdAt: string;
  deleted: boolean;
  ownershipStatus: OrganisationOwnershipStatus;
};

// OrgList
const getOrganisationList = () => request<ZeusOrganisationLite[]>('GET', '/zeus/organisations');

// OrgPage
export type ZeusActiveConnection = {
  id: string;
  createdAt: string;
  orgID: string;
  orgName: string;
  connectionStatus: ConnectionStatus;
  ownershipStatus: OrganisationOwnershipStatus;
  sourceType?: ConnectionSourceType;
};

const getOrganisation = (orgID: string) =>
  request<ZeusOrgData>('GET', `/zeus/organisations/${orgID}`);

const getOrgRoles = (orgID: string) => request<Role[]>('GET', `/zeus/organisations/${orgID}/roles`);

export type OrgPatch = {
  website: string;
};
const patchOrg = (orgID: string, data: OrgPatch) =>
  request('PUT', `/zeus/organisations/${orgID}`, data);

export type OrgProfile = {
  id: string;
  orgID: string;
  type: string;
  createdAt: string;
  createdBy: string;
  modifiedAt: string;
  modifiedBy: string;
  deletedAt: string | null;
  deletedBy: string | null;
};

export type ZeusAddUserRequest = {
  orgID: string;
  email: string;
  firstName: string;
  lastName: string;
  roleID: string;
  isLead: boolean;
  team: string;
  restrictSupplierAccess: boolean;
};

const deleteOrganisation = (orgID: any) => request('DELETE', `/god/orgs/${orgID}`);
const confirmOrganisation = (orgID: any) => request('POST', `/zeus/organisations/${orgID}/confirm`);
const setOrgSuspended = (orgID: any, suspended: any) =>
  request('POST', `/god/orgs/${orgID}/suspended`, { suspended });
const setOrgHidden = (orgID: any, hidden: any) =>
  request('POST', `/god/orgs/${orgID}/hidden`, { hidden });
const setOrgPaid = (orgID: any, paid: any) => request('PUT', `/god/orgs/${orgID}/paid`, { paid });
const addUser = (userData: ZeusAddUserRequest) => request('POST', '/zeus/users', userData);
const deleteUser = (userID: any, deletedReason: any) =>
  request('DELETE', `/zeus/users/${userID}`, { deletedReason });
const getOrgAnswers = (orgID: string) => request<Answer[]>('GET', `/god/orgs/${orgID}/answers`);
const setAssessmentRemindersPause = (orgID: any, assessmentRemindersPause: any) =>
  request('POST', `/god/orgs/${orgID}/assessment-reminders/pause`, { assessmentRemindersPause });
const toggleOrgClientRestricted = (orgID: any) =>
  request('POST', `/zeus/organisations/${orgID}/toggle-client-restrict`);
const toggleMonitoringAssetStatus = (assetID: any) =>
  request('PUT', `/zeus/external-monitoring/assets/${assetID}/toggle-status`);

const getOrgDocuments = (orgID: string, payload: DatasetRequest) =>
  request<DocumentDatasetResponse>('POST', `/zeus/organisations/${orgID}/documents/list`, payload);

// UserList
const searchUsers = (query: any) => request('GET', '/god/users/search', undefined, { query });

// UserPage
export type UserAndOrgOrganisation = {
  orgID: string;
  name: string;
  logoUrl: string;
  status: string;
  isSupplier: boolean;
  isClient: boolean;
};

export type SSOStatus = {
  ssoEnabled: boolean; // Backwards compatibility
  ssoSamlEnabled: boolean;
  ssoOauthEnabled: boolean;
  ssoEnforced: boolean;
  provisioningEnabled: boolean;
  /** @deprecated */
  provisioningRole: string;
  provisioningRoleID: string;
};

export type GodGetUserAndOrgResponse = {
  user: User;
  org: UserAndOrgOrganisation;
  ssoStatus: SSOStatus;
  authenticatorEnabled: boolean;
  cognitoStatus: string;
};

const getUserData = (userID: string) =>
  request<GodGetUserAndOrgResponse>('GET', `/god/users/${userID}`);
const confirmUser = (userID: string) => request('POST', `/zeus/users/${userID}/confirm`);
const editUserData = (userID: string, data: any) => request('PATCH', `/god/users/${userID}`, data);
const generateNewPassword = (userID: string) => request('POST', `/god/users/${userID}/reset-pass`);
const resendTempPassword = (userID: string) =>
  request('POST', `/god/users/${userID}/resend-temp-pass`);
const setUserMFAtoSMS = (userID: string) => request('POST', `/zeus/users/${userID}/set-mfa-to-sms`);
const disableUserMFAPreference = (userID: string) =>
  request('POST', `/zeus/users/${userID}/disable-mfa`);
const resetUserMFAPreference = (userID: string) =>
  request('POST', `/zeus/users/${userID}/reset-mfa`);

export type BulkCreateInviteRequest = {
  orgs: {
    id: string;
    name: string;
    email: string;
    location: string;
    sector: string | undefined;
    website: string;
    creating: boolean;
    error: any;
    created: boolean;
  }[];
  senderUserID: string;
};

const createBulkUnclaimedSuppliers = (req: BulkCreateInviteRequest) =>
  request('POST', '/zeus/suppliers/new', req);

type NewConnectionRequestContact = {
  email: string;
  name: string;
};

export type NewConnectionRequest = {
  orgID: string;
  contact: NewConnectionRequestContact;
  messageOverride: string | null;
};

export type BulkCreateConnectionRequestsRequest = {
  senderUserID: string;
  newConnectionRequests: NewConnectionRequest[];
  message: string;
};

type ErrorResponse = {
  type: string;
  msg: string;
};

export type BulkCreateConnectionRequestsResponse = {
  connectionRequests: ConnectionRequest | null;
  connection: Connection | null;
  errors: ErrorResponse | null;
};

const bulkCreateConnectionRequests = (data: BulkCreateConnectionRequestsRequest) =>
  request<BulkCreateConnectionRequestsResponse[]>('POST', '/zeus/connections/new', data);

// Invite Reminders
const getPendingInvites = () => request('GET', `/zeus/pending-invites`);
const setPendingInviteReminderPauseStatus = (inviteID: any, paused: any) =>
  request('POST', `/zeus/pending-invites/${inviteID}/pause-reminders`, {
    paused,
  });

// Org Invites
export type OrgInvite = {
  id: string;
  senderName: string;
  recipientOrgName: string;
  recipientEmail: string;
  createdAt: string;
  expired: boolean;
  expiresAt?: string;
  accepted: boolean;
  acceptedAt?: string;
  acceptedByOrgID?: string;
  acceptedByOrgLogo?: string;
  resentAt?: string;
  cancelled: boolean;
  cancelledAt?: string;
  unclaimedOrgID: string;
};

export type OrgInvitesParams = {
  orgId: string;
  request: DatasetRequest;
};
export type OrgInvitesResponse = DatasetResponse & {
  invites: OrgInvite[];
};

const getOrgInvites = (orgID: string, payload: DatasetRequest) =>
  request<OrgInvitesResponse>('POST', `/zeus/organisations/${orgID}/invites`, payload);
const resendInvite = (inviteID: string) => request('POST', `/zeus/invites/${inviteID}/resend`);

// Federated
const createFederatedOrg = ({ name, website, country, industry }: any) =>
  request('POST', `/zeus/federated-organisations/create`, { name, website, country, industry });

const getAllFederatedOrgs = () => request('GET', '/zeus/federated-organisations');

// Framework
const getCombinedFrameworkConfigForSupplier = (orgID: string) =>
  request('GET', `/zeus/suppliers/${orgID}/combined-framework-config`);

// Trials
const getApprovedSuppliers = () => request<string[]>('GET', '/zeus/trials/approved-suppliers');

export const live = {
  getOrganisationList,
  searchUsers,
  getUserData,
  confirmUser,
  editUserData,
  generateNewPassword,
  resendTempPassword,
  setUserMFAtoSMS,
  getOrganisation,
  patchOrg,
  deleteOrganisation,
  setOrgSuspended,
  setOrgHidden,
  setOrgPaid,
  getOrgDocuments,
  addUser,
  deleteUser,
  getOrgAnswers,
  getOrgRoles,
  setAssessmentRemindersPause,
  getPendingInvites,
  setPendingInviteReminderPauseStatus,
  getOrgInvites,
  resendInvite,
  createFederatedOrg,
  getAllFederatedOrgs,
  confirmOrganisation,
  createBulkUnclaimedSuppliers,
  disableUserMFAPreference,
  resetUserMFAPreference,
  toggleOrgClientRestricted,
  toggleMonitoringAssetStatus,
  bulkCreateConnectionRequests,
  getCombinedFrameworkConfigForSupplier,
  getApprovedSuppliers,
};

const api = { ...live };
export default api;

export const replaceAPI = (newApi: any) => {
  Object.keys(newApi).forEach((i) => {
    (api as any)[i] = newApi[i];
  });
};
